/**
 * The deep-scan ping (spec §6.2, plan R18, §2.9 flow 8). Holding the scan key accumulates `scan.holdS` while the ship
 * is not travelling, out of combat (no living hostile within `combatHostileM`, and no hit on the player within
 * `combatHitS`, read from the combat ring with this subsystem's own cursor), and, when `deepScanRequiresNoTarget` is
 * set, with no target. At `holdS` it pings: `REV.scanPing` with the scanner Mk's radius (0 = the whole system), and
 * every undiscovered POI within the radius is revealed with its richness. Then `cooldownS`, and the key must be
 * released before the next charge. `scan.blocked` names the current block. Allocation-free apart from a ping.
 */
import { EV, EVENT_MASK, firstReadable } from '../combat/events'
import { isHostile } from '../combat/hostility'
import { SIM_DT } from '../core/loop'
import type { SystemLayout } from '../galaxy/layout/types'
import { emitReach, REV } from '../reach/events'
import type { ReachCore, ReachSubsystem } from '../reach/runtime/types'
import { isPoiDiscovered } from '../reach/state'
import type { Sim } from '../sim'
import { Life, Pos64, Target, Team } from '../world/components'
import { scanRadiusM } from './config'
import { revealPoi, type ExplorationContext } from './context'

/** Tolerance so 3 s of 1/60 s ticks reaches `holdS` despite float rounding. */
const HOLD_EPSILON = 1e-9

/** Records hits on the player since the last call. Allocation-free. */
export function watchHits(x: ExplorationContext, sim: Sim): void {
  const ring = sim.events
  const p = sim.playerEid
  for (let i = firstReadable(ring, x.hitCursor); i < ring.head; i++) {
    const s = i & EVENT_MASK
    const type = ring.type[s]
    if ((type === EV.hitShield || type === EV.hitHull) && ring.eid[s] === p && p >= 0) x.lastHitAt = sim.time
  }
  x.hitCursor = ring.head
}

/** True when a living ship of a team hostile to `p`'s (`isHostile`) is within `radiusM` of ship `p`. Allocation-free. */
export function hostileWithin(sim: Sim, p: number, radiusM: number): boolean {
  const dense = sim.ships.dense
  const team = Team.id[p]
  const r2 = radiusM * radiusM
  for (let i = 0; i < dense.length; i++) {
    const eid = dense[i]
    if (eid === p || Life.alive[eid] !== 1 || !isHostile(team, Team.id[eid])) continue
    const dx = Pos64.x[eid] - Pos64.x[p]
    const dy = Pos64.y[eid] - Pos64.y[p]
    const dz = Pos64.z[eid] - Pos64.z[p]
    if (dx * dx + dy * dy + dz * dz <= r2) return true
  }
  return false
}

/** True while the player counts as in combat (a hostile near, or hit recently). */
export function inCombat(x: ExplorationContext, sim: Sim, p: number): boolean {
  return sim.time - x.lastHitAt < x.cfg.scanner.combatHitS || hostileWithin(sim, p, x.cfg.scanner.combatHostileM)
}

/** Reveals every undiscovered POI within the scanner's radius of the player. Rare (a ping). */
export function ping(x: ExplorationContext, core: ReachCore, sim: Sim, layout: SystemLayout, p: number): number {
  const radius = scanRadiusM(x.cfg, core.career.scannerMk())
  const px = Pos64.x[p]
  const py = Pos64.y[p]
  const pz = Pos64.z[p]
  emitReach(core.events, REV.scanPing, 0, 0, px, py, pz, Number.isFinite(radius) ? radius : 0)
  let revealed = 0
  const r2 = radius * radius
  for (const poi of layout.pois) {
    if (isPoiDiscovered(core.state, layout.systemId, poi.id)) continue
    const dx = poi.position[0] - px
    const dy = poi.position[1] - py
    const dz = poi.position[2] - pz
    if (dx * dx + dy * dy + dz * dz > r2) continue
    if (revealPoi(x, core, layout, poi)) revealed++
  }
  const scan = core.scan
  scan.holdS = 0
  scan.lastPingAt = sim.time
  scan.lastRevealed = revealed
  x.latched = true
  x.cooldownUntil = sim.time + x.cfg.scanner.cooldownS
  return revealed
}

/** A subsystem that can report a selected target (additive, M3 R22j: the M2 bridge's targeted scan). */
interface ScanTargetSource {
  scanTarget?: (core: ReachCore, sim: Sim) => boolean
}

/**
 * Whether a target is selected for the deep scan's no-target rule: M1's combat target, or any subsystem reporting
 * one through `scanTarget` (the M2 bridge: M2's nav target, or a relay under its targeted scan). Allocation-free.
 */
export function hasScanTarget(core: ReachCore, sim: Sim, p: number): boolean {
  if (Target.eid[p] >= 0) return true
  const list = core.subsystems
  for (let i = 0; i < list.length; i++) {
    const s = list[i] as ReachSubsystem & ScanTargetSource
    if (typeof s.scanTarget === 'function' && s.scanTarget(core, sim)) return true
  }
  return false
}

/** One tick of the deep scan. Allocation-free unless it pings. */
export function stepScan(x: ExplorationContext, core: ReachCore, sim: Sim): void {
  watchHits(x, sim)
  const scan = core.scan
  const layout = core.session.layout
  const p = sim.playerEid
  if (layout === null || !core.session.ready || p < 0 || Life.alive[p] !== 1) {
    scan.holdS = 0
    scan.blocked = 'none'
    return
  }
  if (core.travel.mode !== 'normal') scan.blocked = 'travel'
  else if (inCombat(x, sim, p)) scan.blocked = 'combat'
  else if (x.controls.deepScanRequiresNoTarget && hasScanTarget(core, sim, p)) scan.blocked = 'target'
  else scan.blocked = 'none'
  if (!core.input.scanHeld) {
    x.latched = false
    scan.holdS = 0
    return
  }
  if (scan.blocked !== 'none' || x.latched || sim.time < x.cooldownUntil) {
    scan.holdS = 0
    return
  }
  if (scan.holdS === 0) {
    const radius = scanRadiusM(x.cfg, core.career.scannerMk())
    emitReach(core.events, REV.scanCharge, 0, 0, Pos64.x[p], Pos64.y[p], Pos64.z[p], radius < Number.POSITIVE_INFINITY ? radius : 0)
  }
  scan.holdS += SIM_DT
  if (scan.holdS + HOLD_EPSILON >= x.cfg.scanner.holdS) ping(x, core, sim, layout, p)
}

/**
 * M7 S40 (plan S40): the derelict POI a deep-scan ping at (x, y, z) with radius `radiusM` (0 = the whole system)
 * surveys as a targeted scan: the nav target when it is a discovered `derelict` POI of `layout` within the radius.
 * Returns its layout index, or −1. The story link checks the scanner's Mk (Mk II+) and reads the survey card. Rare
 * (a ping); allocation-free.
 */
export function surveyTargetPoi(
  layout: Pick<SystemLayout, 'systemId' | 'pois'>,
  state: Pick<ReachCore['state'], 'navTarget' | 'discoveredPois'>,
  x: number,
  y: number,
  z: number,
  radiusM: number,
): number {
  const nav = state.navTarget
  if (nav === null || nav.kind !== 'poi') return -1
  const pois = layout.pois
  for (let i = 0; i < pois.length; i++) {
    const poi = pois[i]
    if (poi.id !== nav.id) continue
    if (poi.kind !== 'derelict' || !(state.discoveredPois[layout.systemId]?.includes(poi.id) ?? false)) return -1
    if (radiusM <= 0) return i
    const dx = poi.position[0] - x
    const dy = poi.position[1] - y
    const dz = poi.position[2] - z
    return dx * dx + dy * dy + dz * dz <= radiusM * radiusM ? i : -1
  }
  return -1
}
